// Anrop mot adminytan. Varje anrop bär ID-tokenen; ingen cookie, ingen session.
//
// Backenden ligger på en annan origin (Fly) än portalen (Firebase Hosting), så
// ett Authorization-huvud är både det enklaste och det som inte kan skickas av
// misstag från en annan flik.
const BASE = import.meta.env.VITE_API_BASE;

async function call(user, path, options = {}) {
  const token = await user.getIdToken();
  const response = await fetch(`${BASE}${path}`, {
    ...options,
    headers: {
      ...(options.headers || {}),
      Authorization: `Bearer ${token}`,
      ...(options.body ? { "Content-Type": "application/json" } : {}),
    },
  });

  const body = await response.json().catch(() => ({}));
  if (!response.ok) {
    // Felmeddelandet från backenden går rakt fram när det finns ett. De är
    // skrivna för skolans IT — "Den här filen är en inskannad bild utan text"
    // — och att ersätta dem med "Något gick fel" vore att kasta bort det enda
    // som gör felet åtgärdbart.
    const error = new Error(body.message || body.error || `HTTP ${response.status}`);
    error.code = body.error;
    error.status = response.status;
    throw error;
  }
  return body;
}

export const listDocuments = (user) => call(user, "/admin/documents");
export const jobStatus = (user, jobId) => call(user, `/admin/jobs/${jobId}`);
export const unanswered = (user) => call(user, "/admin/unanswered");
export const exportAll = (user) => call(user, "/admin/export");

/**
 * Talar om för backenden att någon loggat in, så att den kan sätta claimen om
 * adressen står på vitlistan. Den ENDA endpointen som anropas utan claim — alla
 * andra kräver den, och den här är hur man får den.
 *
 * Svarar alltid 200. `granted` säger om en claim sattes just nu; i så fall är
 * tokenen vi bär föråldrad och måste hämtas om.
 */
export const startSession = (user) => call(user, "/admin/session", { method: "POST" });

export const createDocument = (user, payload) =>
  call(user, "/admin/documents", { method: "POST", body: JSON.stringify(payload) });

export const deleteDocument = (user, id) =>
  call(user, `/admin/documents/${id}`, { method: "DELETE" });

// --- Vad som faktiskt hamnade i kunskapsbasen (planens 8b) -------------------
//
// `documentText` hämtar chunkarna i ordning, med överlappet avdraget. Det är
// exakt det Ask Aerro läser — inte en ny rendering av filen. Skillnaden är hela
// poängen: en PDF som är halvt textbärande ser hel ut i filen och blir radbrus
// som chunkar, och det syns ingen annanstans.
export const documentText = (user, id, offset = 0) =>
  call(user, `/admin/documents/${id}/text?offset=${offset}`);

// Svarar med en signerad URL, inte med filen. Länken lever fem minuter och
// fungerar för var och en som har den — portalen öppnar den direkt och sparar
// den aldrig. Varje anrop loggas i revisionsloggen.
export const documentDownloadUrl = (user, id) =>
  call(user, `/admin/documents/${id}/download`);

export const dismissJob = (user, jobId) =>
  call(user, `/admin/jobs/${jobId}/dismiss`, { method: "POST" });

// --- Granskningskön för skolans utskick (AA-5d) ------------------------------
//
// Godkännandet bär tre fält, och alla tre prövas i backenden också: titel,
// sista giltighetsdag (H7, obligatorisk) och personuppgiftsbekräftelsen
// (AA-5.3). Att skicka dem härifrån är hur formuläret ser ut — att de GÄLLER
// avgörs i `inbound_review.approve`.
export const listInbound = (user) => call(user, "/admin/inbound");

export const approveInbound = (user, id, payload) =>
  call(user, `/admin/inbound/${id}/approve`,
       { method: "POST", body: JSON.stringify(payload) });

export const rejectInbound = (user, id) =>
  call(user, `/admin/inbound/${id}/reject`, { method: "POST" });

// --- Skolans inställningar och direktpublicering (planens 7B) ----------------
//
// `auto_publish` är skolans beslut och inte vårt: utskick som klarar spärrarna
// publiceras utan att någon granskar dem. Backenden kräver `confirm: true` för
// att slå PÅ, av samma skäl som personuppgiftsrutan vid ett godkännande —
// kravet ska inte bara ligga i en kryssruta i klienten.
export const getSettings = (user) => call(user, "/admin/settings");

export const setAutoPublish = (user, on) =>
  call(user, "/admin/settings", {
    method: "POST",
    body: JSON.stringify({ auto_publish: on, confirm: on ? true : undefined }),
  });

// Det som publicerats utan granskning de senaste dagarna. Inte en kö — det som
// står här är redan ute, och rutan finns för att någon ska märka det.
export const listPublished = (user) => call(user, "/admin/published");

// Skolans mottagningsadress, att lägga in som mottagare i Meitner. Svarar
// `ready: false` med ett skäl när adressen inte finns — att visa en adress som
// inte kan ta emot post vore värre än att visa ingen.
export const getInboundAddress = (user) => call(user, "/admin/inbound-address");
